import { DefaultRouteTabs, Group, SaveButton } from '@/components/_common';
import { Helmet } from 'react-helmet';
import { invoiceCfeRoutePage } from '../routePageConfig';
import { PageProps } from '@/types/PageProps';
import InvoiceCFeRecipientForm from '@/components/Fiscal/Invoice/InvoiceCFe/InvoiceCFeRecipientForm';
import { useInvoiceCFEFormContext } from '@/components/Fiscal/Invoice/InvoiceCFe/InvoiceCFEFormProvider';
import { Button, Form, message } from 'antd';
import { useEffect, useState } from 'react';
import {
  TaxCouponDetailRecipientFormValues,
  TaxCouponStatus,
} from '@/core/domain/coupon/coupons/coupons.types';
import InvoiceCFEHeader from '@/components/Fiscal/Invoice/InvoiceCFe/InvoiceCFEHeader';
import InvoiceCFEFooter from '@/components/Fiscal/Invoice/InvoiceCFe/InvoiceCFEFooter';
import { useTaxCouponRecipienteCreate } from '@/core/domain/coupon/coupons/coupons.hooks';
import { InvoiceRecipientType } from '@/core/domain/invoice/invoices/invoices.enums';
import { getActiveTabPageTaxCoupon } from '@/components/Fiscal/Invoice/InvoiceCFe/getActiveTabPageTaxCoupon';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

const InvoiceCFeRecipientPage: React.FC<PageProps> = ({ route }) => {
  const { taxCoupon } = useInvoiceCFEFormContext();

  const disabled =
    taxCoupon?.status !== TaxCouponStatus.TYPING ||
    !hasPermissions([Authorities.FISCAL_COUPON_EDIT]);

  const [form] = Form.useForm<TaxCouponDetailRecipientFormValues>();

  const taxCouponRecipient = useTaxCouponRecipienteCreate();

  const [isEditRecipient, setIsEditRecipient] = useState(false);

  useEffect(() => {
    if (taxCoupon) {
      form.setFieldsValue({
        id: taxCoupon?.recipient?.id,
        recipientName: taxCoupon?.recipient?.clientName,
        cpfCnpj: taxCoupon?.recipient?.cpfCnpj,
        hasAddress: !!taxCoupon?.recipient?.deliveryAddress?.zipcode,
        deliveryAddress: taxCoupon?.recipient?.deliveryAddress,
      });
    }
  }, [taxCoupon]);

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      if (taxCoupon && values.id && values.cpfCnpj && values.recipientName) {
        const result = await taxCouponRecipient.mutateAsync({
          taxCouponId: taxCoupon?.id,
          id: values?.id,
          cpfCnpj: values?.cpfCnpj,
          clientName: values?.recipientName,
          deliveryAddress: values?.deliveryAddress?.zipcode
            ? {
                ...values?.deliveryAddress,
                country: 'BR',
              }
            : undefined,
        });

        form.setFieldsValue({
          recipientName: result.clientName,
          cpfCnpj: result.cpfCnpj,
          id: result.id,
          deliveryAddress: result.deliveryAddress,
          recipient: undefined,
        });

        form.setFieldValue(
          ['recipient', 'recipientType'],
          InvoiceRecipientType.CUSTOMER,
        );

        setIsEditRecipient(false);
      }
    } catch {
      message.error('Informe os dados do destinatário');
    }
  }

  function cancelChangeRecipient() {
    form.setFieldsValue({
      recipientName: taxCoupon?.recipient?.clientName,
      cpfCnpj: taxCoupon?.recipient?.cpfCnpj,
      id: taxCoupon?.recipient?.id,
      deliveryAddress: taxCoupon?.recipient?.deliveryAddress,
      recipient: undefined,
    });

    form.setFieldValue(
      ['recipient', 'recipientType'],
      InvoiceRecipientType.CUSTOMER,
    );

    setIsEditRecipient(false);
  }

  return (
    <DefaultRouteTabs
      route={invoiceCfeRoutePage(route)}
      permissions={route.permissions}
      {...getActiveTabPageTaxCoupon()}
    >
      <Helmet title="Destinatário" />

      <InvoiceCFEHeader
        extra={
          <>
            {!isEditRecipient && (
              <Button
                onClick={() => setIsEditRecipient(!isEditRecipient)}
                type="primary"
                disabled={disabled}
              >
                Alterar o dados
              </Button>
            )}

            {isEditRecipient && (
              <Group gap={16}>
                <Button onClick={cancelChangeRecipient}>Cancelar</Button>

                <SaveButton
                  loading={taxCouponRecipient.isLoading}
                  onClick={handleSubmit}
                >
                  Salvar
                </SaveButton>
              </Group>
            )}
          </>
        }
      />

      <InvoiceCFeRecipientForm
        form={form}
        isEditRecipient={
          isEditRecipient && hasPermissions([Authorities.FISCAL_COUPON_EDIT])
        }
      />

      <InvoiceCFEFooter />
    </DefaultRouteTabs>
  );
};

export default InvoiceCFeRecipientPage;
